/**
 * Prevent duplicate hydration calls for the same view within a
 * short time window.  When navigationController, contextLoaders,
 * and prototypeHydration all fire on the same view switch, only
 * the first call proceeds; subsequent calls within the debounce
 * window are skipped.
 *
 * The window is intentionally short (100ms) — just enough to collapse
 * the rapid-fire duplicate calls from a single navigation event
 * (click handler → applyInitialViewFromUrl → hydratePrototypeRuntime).
 * Longer windows (the previous 800ms) caused user-visible bugs where
 * navigating away and back within ~1s skipped data reload entirely
 * (GitHub trending was the first report; Community had the same code
 * path but was less noticeable because its data changed more often).
 */

const pending = new Map<string, number>();
const DEBOUNCE_MS = 100;

export function shouldHydrate(viewKey: string): boolean {
  const now = Date.now();
  const last = pending.get(viewKey);
  if (last && now - last < DEBOUNCE_MS) return false;
  pending.set(viewKey, now);
  return true;
}

/**
 * Clear the hydration guard for a specific view key (or all keys).
 * Called from showView() on view switch so the new view's first
 * hydrator call always proceeds, regardless of when the previous
 * visit happened.
 */
export function resetHydrateGuard(viewKey?: string): void {
  if (viewKey) {
    pending.delete(viewKey);
  } else {
    pending.clear();
  }
}
